import { getCurrencySymbol } from "../pages/LawyerPages/Settings/CurrencySetForm";

export const normalizeCurrencyCode = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toUpperCase();
};

export const resolveCurrencyConfig = (currencyCode, fallbackCurrency = "USD") => {
  const code =
    normalizeCurrencyCode(currencyCode) ||
    normalizeCurrencyCode(fallbackCurrency) ||
    "USD";

  return {
    code,
    symbol: getCurrencySymbol(code) || code,
  };
};

const buildConfiguredCurrencyOption = (currency) => {
  const value = normalizeCurrencyCode(
    currency?.shortForm || currency?.value || currency?.currency,
  );

  if (!value) return null;

  const symbol = currency?.symbol || getCurrencySymbol(value) || value;
  const name = currency?.name || value;

  return {
    label: `${value} - ${name}${symbol && symbol !== value ? ` (${symbol})` : ""}`,
    value,
    symbol,
    name,
    isDefault: Boolean(currency?.isDefault),
  };
};

const dedupeCurrencyOptions = (options) => {
  const seen = new Set();

  return options.filter((option) => {
    const value = normalizeCurrencyCode(option?.value);
    if (!value || seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
};

export const buildCurrencyOptions = ({
  userCurrencies = [],
  includeValue = null,
} = {}) => {
  const configuredOptions =
    Array.isArray(userCurrencies) && userCurrencies.length > 0
      ? userCurrencies
          .map(buildConfiguredCurrencyOption)
          .filter(Boolean)
          .sort((left, right) => Number(right.isDefault) - Number(left.isDefault))
      : [];

  const requiredValue = normalizeCurrencyCode(includeValue);
  const fallbackOptions = [...configuredOptions];

  if (!requiredValue) {
    return dedupeCurrencyOptions(fallbackOptions);
  }

  const hasValue = fallbackOptions.some(
    (option) => normalizeCurrencyCode(option?.value) === requiredValue,
  );

  if (!hasValue) {
    fallbackOptions.push({
      label: `${requiredValue} (${getCurrencySymbol(requiredValue) || requiredValue})`,
      value: requiredValue,
      symbol: getCurrencySymbol(requiredValue) || requiredValue,
      name: requiredValue,
      isDefault: false,
    });
  }

  return dedupeCurrencyOptions(fallbackOptions);
};

export const getConfiguredCurrencyCodes = (userCurrencies = []) => {
  if (!Array.isArray(userCurrencies)) return [];

  return dedupeCurrencyOptions(
    userCurrencies.map((currency) => ({
      value: currency?.shortForm || currency?.value || currency?.currency,
    })),
  ).map((option) => normalizeCurrencyCode(option.value));
};

export const isCurrencyConfigured = (userCurrencies = [], currencyCode) => {
  const normalizedCode = normalizeCurrencyCode(currencyCode);
  if (!normalizedCode) return false;

  return getConfiguredCurrencyCodes(userCurrencies).includes(normalizedCode);
};

const formatNumericValue = (
  value,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  });

export const formatCurrencyAmount = (
  value,
  currencyCode,
  options = {},
) => {
  const { code, symbol } = resolveCurrencyConfig(currencyCode);
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    ...restOptions
  } = options;

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits,
      ...restOptions,
    }).format(Number(value || 0));
  } catch (error) {
    return `${symbol}${formatNumericValue(
      value,
      minimumFractionDigits,
      maximumFractionDigits,
    )}`;
  }
};

export const formatCompactCurrencyAmount = (
  value,
  currencyCode,
  options = {},
) =>
  formatCurrencyAmount(value, currencyCode, {
    notation: "compact",
    maximumFractionDigits: 1,
    ...options,
  });
